import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBeer,
  deleteBeer,
  deleteBeerPrice,
  generateBeerDescriptions,
  listBeerPrices,
  listBeers,
  patchBeer,
  suggestStyles,
  suggestTags,
  uploadBeerArtwork,
  upsertBeerPrice,
} from '../api/client';
import type { CreateBeerBody, PatchBeerBody, UpsertBeerPriceBody } from '../api/client';
import type { Beer } from '../api/types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Full paginated beer list with filters. */
export function useBeers(params?: {
  status?: string;
  includeRetired?: boolean;
  tags?: string[];
  search?: string;
  sort?: string;
  offset?: number;
  limit?: number;
}) {
  const { includeRetired, ...rest } = params ?? {};
  const queryParams = includeRetired ? rest : { status: rest.status ?? 'active', ...rest };
  return useQuery({
    queryKey: ['beers', 'list', queryParams],
    queryFn: () => listBeers({ limit: 50, ...queryParams }),
    staleTime: 30_000,
  });
}

/** Beer counts by status — for the Brewer home screen. */
export function useBreweryStats() {
  const active = useQuery({
    queryKey: ['beers', 'count', 'active'],
    queryFn: () => listBeers({ status: 'active', limit: 1 }),
    staleTime: 2 * 60_000,
  });
  const pending = useQuery({
    queryKey: ['beers', 'count', 'pending'],
    queryFn: () => listBeers({ status: 'pending', limit: 1 }),
    staleTime: 2 * 60_000,
  });
  const retired = useQuery({
    queryKey: ['beers', 'count', 'retired'],
    queryFn: () => listBeers({ status: 'retired', limit: 1 }),
    staleTime: 2 * 60_000,
  });

  return {
    activeCount: active.data?.total ?? null,
    pendingCount: pending.data?.total ?? null,
    retiredCount: retired.data?.total ?? null,
    isLoading: active.isLoading || pending.isLoading || retired.isLoading,
    error: active.error ?? pending.error ?? retired.error ?? null,
  };
}

/** Fetch all active beers; returns a Map keyed by ID for O(1) lookup. */
export function useBeersMap() {
  const query = useQuery({
    queryKey: ['beers', 'active', 'all'],
    queryFn: () => listBeers({ limit: 200 }),
    staleTime: 2 * 60_000,
  });

  const beerMap = new Map<string, Beer>(
    query.data?.items.map((b) => [b.id, b]) ?? [],
  );

  return { ...query, beerMap };
}

/** Per-location prices for a single beer. */
export function useBeerPrices(beerId: string | null) {
  return useQuery({
    queryKey: ['beers', beerId, 'prices'],
    queryFn: () => listBeerPrices(beerId!),
    enabled: beerId != null,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateBeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBeerBody) => createBeer(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beers'] });
    },
  });
}

export function usePatchBeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beerId, body }: { beerId: string; body: PatchBeerBody }) =>
      patchBeer(beerId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beers'] });
    },
  });
}

export function useDeleteBeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (beerId: string) => deleteBeer(beerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beers'] });
    },
  });
}

export function useUploadArtwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beerId, file, onProgress }: {
      beerId: string;
      file: File;
      onProgress?: (pct: number) => void;
    }) => uploadBeerArtwork(beerId, file, onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beers'] });
    },
  });
}

export function useUpsertBeerPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beerId, locationId, body }: {
      beerId: string;
      locationId: string;
      body: UpsertBeerPriceBody;
    }) => upsertBeerPrice(beerId, locationId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['beers', vars.beerId, 'prices'] });
    },
  });
}

export function useDeleteBeerPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ beerId, locationId }: { beerId: string; locationId: string }) =>
      deleteBeerPrice(beerId, locationId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['beers', vars.beerId, 'prices'] });
    },
  });
}

export function useGenerateBeerDescriptions() {
  return useMutation({
    mutationFn: generateBeerDescriptions,
  });
}

// ---------------------------------------------------------------------------
// Autocomplete helpers (debounced in the component; raw fetchers here)
// ---------------------------------------------------------------------------

export function useSuggestStyles(q: string) {
  return useQuery({
    queryKey: ['beers', 'styles', 'suggest', q],
    queryFn: () => suggestStyles(q),
    enabled: q.length >= 1,
    staleTime: 60_000,
  });
}

export function useSuggestTags(q: string) {
  return useQuery({
    queryKey: ['beers', 'tags', 'suggest', q],
    queryFn: () => suggestTags(q),
    enabled: q.length >= 1,
    staleTime: 60_000,
  });
}
